<template>
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Work</div>
                <div class="page-bar-main">Training</div>
            </div>
            <div style="display:flex;gap:8px;align-items:center">
                <forge-page-button :kinds="['training']" label="Plan my session" @applied="loadItems()" />
                <div v-if="$store.state.user.teamHubRole == 'Admin' || $store.state.user.teamHubEmployee?.IsManager" @click="showAddEditFn()" class="add-btn">Plan session</div>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <div class="toolbar">
                <div class="seg">
                    <button @click.prevent="() => { completed = false; loadItems() }" :class="completed ? '' : 'on'">Upcoming</button>
                    <button @click.prevent="() => { completed = true; loadItems() }" :class="completed ? 'on' : ''">Completed</button>
                </div>
                <div class="spacer"></div>
            </div>
            <div style="margin:20px 0 20px">Plan a <b>classroom</b> session (group review with a timer) or an <b>on-site</b> session (hands-on at the station, signed off per person). When it's time to run it, choose who's involved.</div>
            <div>
                <div v-for="(c, i) in classrooms" :key="i" @click="() => { $router.push('/classroom/'+c.ID) }" class="cl-card">
                    <div class="cl-date">
                        <div class="cl-mon">{{ c.month }}</div>
                        <div class="cl-day">{{ c.date }}</div>
                        <div class="cl-yr">{{ c.year }}</div>
                    </div>
                    <div style="flex:1">
                        <div class="cl-nm">{{ c.Name }} <span :class="'tr-type ' + (c.Type == 'On-site' ? 'onsite' : 'classroom')"><svg-icon :icon="c.Type == 'On-site' ? 'wrench' : 'classroom'" />{{ c.Type == 'On-site' ? 'On-site' : 'Classroom' }}</span></div>
                        <div class="cl-sub">{{ c.subLine }}</div>
                    </div>
                    <div class="cl-right">
                        <span v-if="c.timeString" style="font-size:12.5px">{{ c.timeString }}</span>
                        <span :class="'pill ' + c.pill"><svg-icon :icon="c.pillIcon" />{{c.Status }}</span>
                        <svg-icon style="width: 10px;height:10px" icon="chevright" />
                    </div>
                </div>
            </div>
        </div>

        <div v-if="showAddEdit" class="error-msg-wrap">
            <div class="form-wrapper slide-up">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="() => { showAddEdit = false }">&times;</button>
                </div>
                <h3>Type</h3>
                <div class="tr-toggle">
                    <button type="button" :class="'tr-toggle-btn' + (formData.Type == 'Classroom' ? ' on' : '')" @click="() => { formData.Type = 'Classroom' }">
                        <svg-icon icon="classroom" />
                        <div>
                            <div class="ttb-t">Classroom</div>
                            <div class="ttb-d">Group review with a timer</div>
                        </div>
                    </button>
                    <button type="button" :class="'tr-toggle-btn' + (formData.Type == 'On-site' ? ' on' : '')" @click="() => { formData.Type = 'On-site' }">
                        <svg-icon icon="wrench" />
                        <div>
                            <div class="ttb-t">On-site</div>
                            <div class="ttb-d">Hands-on, signed off per person</div>
                        </div>
                    </button>
                </div>
                <h3 style="margin-top:15px">Name</h3>
                <input type="text" :placeholder="formData.Type == 'On-site' ? 'e.g. Expander Line 2 -- Hands-On' : 'e.g. New Hire Onboarding -- Production'" v-model="formData.Name" />
                <template v-if="formData.Type == 'On-site'">
                    <h3>Station / Location</h3>
                    <input type="text" placeholder="e.g. Expander Line 2 · Cincinnati" v-model="formData.Station" />
                </template>
                <h3>Date</h3>
                <input type="date" v-model="formData.Date" />
                <template v-if="formData.Type == 'On-site'">
                    <h3>What We're Reviewing</h3>
                    <input type="text" placeholder="Short description of what you'll cover at the station" v-model="formData.ReviewingText" />
                </template>
                <h3>{{ formData.Type == 'On-site' ? 'Related Processes' : 'Processes to Review' }} <span class="primary">{{ formData.ProcessesJSON.length }}</span> <span v-if="formData.Type == 'On-site'" style="font-weight:normal">· optional</span></h3>
                <input type="text" v-model="processSearchQ" @input="filterProcesses()" placeholder="Search processes" />
                <div style="max-height: 200px; overflow: auto;">
                    <div v-for="(p, i) in processes" :key="i" style="margin-bottom:5px" class="checkbox-input">
                        <input type="checkbox" v-model="p.checked" @change="setProcessesJSON()" />
                        <div>{{ p.Name }}</div>
                    </div>
                </div>
                <div v-if="formData.Type == 'On-site'" style="margin:20px 0 0;font-size:14px;"><svg-icon icon="ibubble2" style="width:14px;height:14px" />Trainees are picked at the station and signed off one by one.</div>
                <div v-else style="margin:20px 0 0;font-size:14px;"><svg-icon icon="ibubble2" style="width:14px;height:14px" />Attendees are chosen when you start the session, so you can take whoever is actually in the room.</div>
                <input style="margin-top:25px" :class="disableButton ? 'disable-button' : ''" type="submit" :value="formData.Type == 'On-site' ? 'Create On-site Session' : 'Create Classroom'" @click="submitAddEdit()" />
            </div>
        </div>
    </div>    
</template>

<script lang="ts">
import { defineComponent, registerRuntimeCompiler } from 'vue';
import axios from 'axios';
import SvgIcon from '@/components/SvgIcon.vue';
import ForgePageButton from '@/components/ForgePageButton.vue';

export default defineComponent({
    name: 'ClassroomsView',
    components: {
        SvgIcon,
        ForgePageButton,
    },
    data() {
        return {
            completed: false,
            showAddEdit: false,
            disableButton: false,
            formData: {
                ID: null as any,
                Name: null as any,
                Date: null as any,
                ProcessesJSON: [] as any,
                Type: 'Classroom' as any,
                Station: null as any,
                ReviewingText: null as any,
            },
            processesAll: [] as any,
            processes: [] as any,
            processSearchQ: '',
            classrooms: [] as any
        }
    },
    async created() {
        while(!this.$store.state.allNodesLoaded) {
            await new Promise(resolve => setTimeout(resolve, 200));
        }

        await this.loadItems();

        this.processesAll = JSON.parse(JSON.stringify(this.$store.state.allNodes));
        this.processes = this.processesAll;

        this.$store.state.routeLoaded = true;
    },
    methods: {
        async loadItems() {
            let url = `${this.$store.state.apiUrl}/api/classroom`;
            if(this.completed) {
                url += '?completed=t';
            }

            await axios.get(url)
                .then(resp => {
                    for(const c of resp.data) {
                        const date = new Date(c.Date);
                        c.month = this.$store.state.monthShortStrings[date.getUTCMonth() + 1].toUpperCase();
                        c.date = date.getUTCDate();
                        c.year = date.getUTCFullYear();
                        if(c.ProcessesJSON) {
                            c.ProcessesJSON = JSON.parse(c.ProcessesJSON);
                        }
                        if(c.TimestampsJSON) {
                            c.TimestampsJSON = JSON.parse(c.TimestampsJSON);
                            let now = new Date().getTime();
                            let pauses = [];
                            if(c.TimestampsJSON.pauses && c.TimestampsJSON.pauses.length) {
                                // currently in a pause
                                if(c.TimestampsJSON.pauses[c.TimestampsJSON.pauses.length - 1].type == 'start') {
                                    now = c.TimestampsJSON.pauses[c.TimestampsJSON.pauses.length - 1].ms;
                                }
                                for(let i = 0; i < c.TimestampsJSON.pauses.length; i += 2) {
                                    if(c.TimestampsJSON.pauses[i + 1]) {
                                        pauses.push(c.TimestampsJSON.pauses[i + 1].ms - c.TimestampsJSON.pauses[i].ms);
                                    }
                                }
                            }
                            if(c.TimestampsJSON.end) {
                                now = c.TimestampsJSON.end;
                            }
                            let ms = now - c.TimestampsJSON.start;
                            for(const pause of pauses) {
                                ms -= pause;
                            }
                            const hours = Math.floor(ms / (1000 * 60 * 60));
                            const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
                            if(hours > 0) {
                                c.timeString = `${hours}h ${minutes}m`
                            } else {
                                c.timeString = `${minutes}m`
                            }
                        }
                        if(c.AttendeesJSON) {
                            c.AttendeesJSON = JSON.parse(c.AttendeesJSON);
                        }
                        if(c.Type == 'On-site') {
                            c.subLine = c.Station ? `${c.Station} · ` : '';
                            c.subLine += c.AttendeesJSON && c.AttendeesJSON.length ? `${c.AttendeesJSON.length} trainees` : 'trainees picked at station';
                        } else {
                            c.subLine = `${c.ProcessesJSON.length} processes · `;
                            if(c.AttendeesJSON) {
                                c.subLine += `${c.AttendeesJSON.length} attendees`;
                            } else {
                                c.subLine += `attendees picked at start`;
                            }
                        }
                        if(c.Status == 'Completed') {
                            c.pill = 'p-done';
                            c.pillIcon = 'check2'
                        } else if(c.Status == 'In progress') {
                            c.pill = 'p-live';
                            c.pillIcon = 'play2';
                        } else if(c.Status == 'Upcoming') {
                            c.pill = 'p-up';
                            c.pillIcon = 'calendar';
                        }
                    }
                    this.classrooms = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async submitAddEdit() {
            if(!this.formData.Name) {
                alert('Add a name for this session');
                return;
            }
            if(!this.formData.Date) {
                alert('Add a date for this session');
                return;
            }
            
            if(this.formData.Type != 'On-site' && !this.formData.ProcessesJSON.length) {
                alert('Select at least one process');
                return;
            }

            this.disableButton = true;

            await axios.post(`${this.$store.state.apiUrl}/api/classroom`, this.formData)
                .then(resp => {
                    this.completed = false;
                    this.loadItems();
                    this.showAddEdit = false;
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error')
                })

            this.disableButton = false;
        },
        setProcessesJSON() {
            const processes = [];
            for(const p of this.processesAll) {
                if(p.checked) {
                    processes.push({
                        slug: p.Slug
                    })
                }
            }
            this.formData.ProcessesJSON = processes;
        },
        filterProcesses() {
            if(this.processSearchQ.length < 2) {
                this.processes = this.processesAll;
            } else {
                const searchLower = this.processSearchQ.toLowerCase();
                this.processes = this.processesAll.filter((p: any) => {
                    const pSearch = `${p.Name}`.toLowerCase();
                    return pSearch.includes(searchLower);
                })
            }
        },
        showAddEditFn() {
            for(const p of this.processesAll) {
                p.checked = false;
            }
            this.formData = {
                ID: null,
                Name: null,
                Date: null,
                ProcessesJSON: [],
                Type: 'Classroom',
                Station: null,
                ReviewingText: null
            }
            this.showAddEdit = true;
        }
    }
})
</script>